import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";

const PLANS = [
  {
    tier: { it: "Base", en: "Free" },
    price: "€0",
    fee: "8%",
    subtitle: { it: "Vetrina + prenotazioni dirette", en: "Showcase + direct bookings" },
    perks: {
      it: ["Vetrina bilingue", "Stripe integrato", "Sync iCal", "Scambio camere"],
      en: ["Bilingual showcase", "Stripe integrated", "iCal sync", "Room swap"],
    },
  },
  {
    tier: { it: "Pro", en: "Pro" },
    price: "€49",
    fee: "6%",
    featured: true,
    subtitle: { it: "Analytics + AI + Channel Manager", en: "Analytics + AI + Channel Manager" },
    perks: {
      it: ["Tutto di Base", "Channel Manager OTA", "AI Chatbot Gemini", "Analytics avanzati"],
      en: ["Everything in Free", "OTA Channel Manager", "Gemini AI Chatbot", "Advanced analytics"],
    },
  },
  {
    tier: { it: "Enterprise", en: "Enterprise" },
    price: "€199",
    fee: "5%",
    subtitle: { it: "Multi-property + white label + Assistente IA", en: "Multi-property + white label + AI Assistant" },
    perks: {
      it: ["Tutto di Pro", "Hospitality Assistant IA", "Fino a 60 camere", "White label + dominio", "Account manager dedicato"],
      en: ["Everything in Pro", "Hospitality Assistant AI", "Up to 60 rooms", "White label + domain", "Dedicated account manager"],
    },
  },
];

/**
 * Compact pricing preview — 3 plan cards side-by-side. Used inside the host
 * onboarding page and the host dashboard to keep the plans visible without
 * navigating to a dedicated `/pricing` page.
 */
export default function PricingPreview({ compact = false, showLink = true }) {
  const { lang } = useLang();
  const IT = lang === "it";
  // Sidebar / compact variant → single-column stacked rows so the text stays legible.
  if (compact) {
    return (
      <div data-testid="pricing-preview" className="space-y-2">
        {PLANS.map((p) => (
          <Link
            key={p.tier.it} to="/pricing"
            data-testid={`preview-plan-${p.tier.it.toLowerCase()}`}
            className={`group flex items-center justify-between border rounded-lg p-3 lift ${p.featured ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}
          >
            <div className="min-w-0">
              <div className="uppercase tracking-[0.15em] text-[9px] text-sage font-semibold flex items-center gap-1">
                {p.tier[IT ? "it" : "en"]}
                {p.featured && <span className="text-[8px] uppercase tracking-widest bg-primary text-primary-foreground rounded-full px-1.5 py-0">{IT ? "Consigliato" : "Popular"}</span>}
              </div>
              <div className="flex items-baseline gap-1.5 mt-0.5">
                <span className="font-display text-lg leading-tight">{p.price}</span>
                {p.price !== "€0" && <span className="text-[10px] text-muted-foreground">/{IT ? "mese" : "mo"}</span>}
                <span className="text-[10px] text-muted-foreground">· fee {p.fee}</span>
              </div>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-primary shrink-0 group-hover:translate-x-0.5 transition-transform"/>
          </Link>
        ))}
        {showLink && (
          <Link to="/pricing" className="block text-center text-[11px] text-primary hover:underline pt-1" data-testid="pricing-preview-more">
            {IT ? "Confronta i dettagli →" : "See all details →"}
          </Link>
        )}
      </div>
    );
  }
  return (
    <div data-testid="pricing-preview">
      <div className="grid md:grid-cols-3 gap-4">
        {PLANS.map((p) => (
          <div
            key={p.tier.it}
            className={`relative rounded-lg border p-5 bg-card flex flex-col ${p.featured ? "border-primary shadow-md" : "border-border"}`}
            data-testid={`preview-plan-${p.tier.it.toLowerCase()}`}
          >
            {p.featured && (
              <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[9px] uppercase tracking-widest rounded-full px-2 py-0.5 shadow">
                {IT ? "Consigliato" : "Popular"}
              </div>
            )}
            <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold">{p.tier[IT ? "it" : "en"]}</div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="font-display text-3xl">{p.price}</span>
              {p.price !== "€0" && <span className="text-xs text-muted-foreground">/{IT ? "mese" : "mo"}</span>}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1 mb-3">{p.subtitle[IT ? "it" : "en"]}</p>
            <div className="text-[10px] bg-sand rounded px-2 py-1 flex items-center justify-between mb-3">
              <span className="uppercase tracking-widest text-muted-foreground">{IT ? "Fee prenotazioni" : "Booking fee"}</span>
              <span className="text-primary font-semibold">{p.fee}</span>
            </div>
            <ul className="space-y-1 text-xs flex-1">
              {p.perks[IT ? "it" : "en"].map((perk) => (
                <li key={perk} className="flex items-start gap-1.5">
                  <Check className="w-3 h-3 text-sage mt-0.5 shrink-0"/>
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {showLink && (
        <div className="text-center mt-3">
          <Button asChild variant="link" size="sm" className="text-primary" data-testid="pricing-preview-more">
            <Link to="/pricing">
              {IT ? "Confronta tutti i dettagli e piani" : "See all plan details"} <ArrowRight className="w-3 h-3 ml-1"/>
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}
